import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getNotifPrefs } from "@/lib/notifPrefs";
import { showAppNotification } from "@/lib/notify";

const MailContext = createContext({ unread: 0, refreshUnread: () => {} });

export function MailProvider({ children }) {
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [unread, setUnread] = useState(0);
  const prevRef = useRef(null);
  const navigate = useNavigate();

  const refreshUnread = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/mail/unread-count");
      const count = data.count || 0;
      const prev = prevRef.current;
      if (prev !== null && count > prev) {
        const diff = count - prev;
        const prefs = getNotifPrefs();
        if (prefs.mail) {
          const body = diff === 1 ? "Hai ricevuto una nuova email" : `Hai ricevuto ${diff} nuove email`;
          const hidden = typeof document !== "undefined" && document.visibilityState === "hidden";
          if (hidden && prefs.browser) {
            // App in background → OS/desktop notification with the app logo
            showAppNotification("Hi-Work", body, { tag: "hiwork-mail", url: "/mail" });
          } else {
            // App in foreground → in-app toast with quick action
            toast.info(body, {
              description: "info@hisocialitalia.it",
              action: { label: "Apri", onClick: () => navigate("/mail") },
            });
          }
        }
      }
      prevRef.current = count;
      setUnread(count);
    } catch {
      /* keep previous value on error */
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin) {
      prevRef.current = null;
      setUnread(0);
      return;
    }
    refreshUnread();
    const iv = setInterval(refreshUnread, 60 * 1000);
    return () => clearInterval(iv);
  }, [isAdmin, refreshUnread]);

  return (
    <MailContext.Provider value={{ unread, refreshUnread }}>
      {children}
    </MailContext.Provider>
  );
}

export const useMail = () => useContext(MailContext);
